import {QuotePriceLine, QuotePriceRider} from "../../../../../api/QuotePricing";
import {PriceLine} from "../../../../../api/Pricing";
import {orderByStrAscending, sum} from "nate-react-api-helpers";
import {PriceLine2} from "../../../shopdrawing/pricing/Frames";
import {Item, SubCategories, TQuotePage} from "../QuotePDF";
import {PriceSectionNote} from "../../../../../api/PriceSectionNotes";
import {MiscPrice} from "../../../../../api/MiscPricing";
import {QuoteMiscPrice} from "../../../../../api/QuoteMiscPricing";

type SubCategoryCollector = {
    group: string;
    rows: PriceLine[]|QuotePriceLine[];
    riders: QuotePriceRider[];
    misc: MiscPrice[]|QuoteMiscPrice[];
    categories: SubCategories[];
    totalCents: number;
}

export class QuotePDFBuilder {
    out: Item[] = [];
    ridersTouched: QuotePriceRider[] = []
    totalCents = 0;
    totalCostCents = 0;
    lastGroup: string | undefined = undefined;
    pages: TQuotePage[] | undefined = undefined;
    addedNewSectionFlag: boolean = false;
    div10SubCategories: SubCategoryCollector[] = [];
    miscSubCategories: SubCategoryCollector[] = [];
    excludeRiders: boolean = false;

    hasPage(p: TQuotePage): boolean {
        if(this.pages === undefined) return true;
        return this.pages.includes(p);
    }

    compile() {
        return this.out.slice(0);
    }

    addTotal() {
        this.out.push({
            type: "total",
            totalCents: this.totalCents,
            totalCostCents: this.totalCostCents,
            group: this.lastGroup,
        });
    }

    completeCategoryBuilding() {
        this.completeDiv10CategoryBuilding();
        this.completeMiscCategoryBuilding();
    }

    completeMiscCategoryBuilding() {
        orderByStrAscending(this.miscSubCategories, s => s.group);

        let allRows: PriceLine[]|QuotePriceLine[] = [];
        let allRiders: QuotePriceRider[] = [];
        let allMisc: MiscPrice[]|QuoteMiscPrice[] = [];
        let categories: SubCategories[] = [];
        let totalCents = 0;

        this.miscSubCategories.forEach(d => {
            allRows.push(...d.rows as any);
            allRiders.push(...d.riders as any);
            allMisc.push(...d.misc as any);

            categories.push({
                name: d.group,
                filter: (i: QuotePriceLine) => false,
                filterRider: (r: QuotePriceRider) => false,
                filterMisc: (r: QuoteMiscPrice) => r.name === d.group,
                adminExcludeRiders: false,
                children: d.categories,
            });

            totalCents += d.totalCents;
        })

        if(this.miscSubCategories.length > 0) {
            this.out.push({
                type: "section-meta",
                value: "Misc",
                group: "Misc",
                rows: allRows,
                riders: allRiders,
                misc: allMisc,
                categories: categories,
                totalCents: totalCents,
            })
        }
    }

    completeDiv10CategoryBuilding() {
        orderByStrAscending(this.div10SubCategories, s => s.group)

        let allRows: PriceLine[]|QuotePriceLine[] = [];
        let allRiders: QuotePriceRider[] = [];
        let allMisc: MiscPrice[]|QuoteMiscPrice[] = [];
        let categories: SubCategories[] = [];
        let totalCents = 0;

        this.div10SubCategories.forEach(d => {
            allRows.push(...d.rows as any);
            allRiders.push(...d.riders as any);
            allMisc.push(...d.misc as any);

            categories.push({
                name: d.group,
                filter: (i: QuotePriceLine) => {
                    return !!d.categories.find(c => c.filter(i));
                },
                filterRider: (r: QuotePriceRider) => {
                    return !!d.categories.find(c => c.filterRider(r));
                },
                adminExcludeRiders: false,
                children: d.categories,
            });

            totalCents += d.totalCents;
        })

        if(this.div10SubCategories.length > 0) {
            this.out.push({
                type: "section-meta",
                value: "Div 10",
                group: "Div 10",
                rows: allRows,
                riders: allRiders,
                misc: allMisc,
                categories: categories,
                totalCents: totalCents,
            })
        }
    }

    addGeneralNotes(notes: PriceSectionNote[]) {
        const nt = notes.find(n => n.keyName === "summary")
        if(nt && !!nt.note) {
            const notes = nt.note.split("\n");
            notes.map((nt, index) => {
                this.out.push({
                    type: "generalNoteSection",
                    isFirst: index === 0,
                    value: nt,
                    group: undefined,
                });
            })
        }
    }

    addSection(input: {
        title: string, note: string[],
        subTotal?: number,
        categories: (SubCategories|false)[];
        div10SubCategory?: string;
        miscSubCategory?: string;
        includeIfNoRows?: boolean,
    }, rows: PriceLine[]|QuotePriceLine[], riders: QuotePriceRider[], misc: MiscPrice[]|QuoteMiscPrice[] = []) {
        this.addedNewSectionFlag = false;
        if(rows.length === 0 && !input.includeIfNoRows) return;

        this.addedNewSectionFlag = true;
        this.lastGroup = input.title;

        let price = input.subTotal || sum(rows.map(r => r.extendedPrice)) || misc.reduce((acc, r) => acc + r.extendedPrice, 0);
        if(!this.excludeRiders) {
            price += riders.reduce((acc, r) => acc + r.extendedPrice, 0);
        }

        let cost = sum(rows.map(r => r.unitCostCents * r.qty));
        if(!this.excludeRiders) {
            cost += riders.reduce((acc, r) => acc + (r.unitCostCents * r.qty), 0);
        }
        cost += misc.reduce((acc, r) => acc + r.unitCostCents, 0);

        this.ridersTouched.push(...riders);

        this.out.push({
            type: "title",
            value: input.title,
            group: input.title,
        })

        if(input.div10SubCategory) {
            this.div10SubCategories.push({
                group: input.div10SubCategory,
                rows: rows,
                riders: riders,
                misc: misc,
                categories: removeBlanksCategories(input.categories),
                totalCents: price,
            });
        } else if (input.miscSubCategory) {
            this.miscSubCategories.push({
                group: input.miscSubCategory,
                rows: rows,
                riders: riders,
                misc: misc,
                categories: removeBlanksCategories(input.categories),
                totalCents: price,
            });
        } else {
            this.out.push({
                type: "section-meta",
                value: input.title,
                group: input.title,
                rows: rows,
                riders: riders,
                misc: misc,
                categories: removeBlanksCategories(input.categories),
                totalCents: price,
            })
        }

        this.out.push(...rows
            .filter((r: PriceLine|QuotePriceLine|PriceLine2) => r.productType !== "frame-prep" && r.productType !== "door-prep")
            .map(r => ({
                type: "row" as "row",
                value: r,
                group: input.title,
            } as Item)))

        if(rows.length > 0 && !!input.subTotal) throw new Error("Can't have both rows and subTotal");

        this.totalCostCents += cost;
        this.totalCents += price;

        let notes = this.autoSplitNotes(input.note);
        if(notes.length > 4) {
            for(let note of notes) {
                this.out.push({
                    type: "text",
                    notes: [note],
                    group: input.title,
                });
            }

            notes = [];
        }

        this.out.push({
            type: "footer",
            totalCents: price,
            notes: notes,
            group: input.title,
        })
    }

    autoSplitNotes(input: string[]) {
        let out: string[] = [];
        for(let i = 0; i < input.length; i++) {
            const note = input[i];
            const split = note.split(/\r*\n/).map(r => r + "\n");
            out.push(...split);
        }

        return out;
    }
}

export function autoRecombine(list: Item[]): Item[] {
    const out: Item[] = [];
    let current: Item | undefined = undefined;

    for(let i = 0; i < list.length; i++) {
        const item = Object.assign({}, list[i]);

        if(current && item.group === current.group) {
            if (item.type === "text") {
                if (current && current.type === "text") {
                    current.notes = [...current.notes, ...item.notes];
                    continue
                }
            }

            if (item.type === "footer") {
                // use footer instead of text
                if (current && current.type === "text") {
                    const notes = current.notes
                    out.pop();
                    item.notes = [...notes, ...item.notes];
                    current = item;
                    out.push(item);
                    continue
                }
            }
        }

        current = item;
        out.push(item);
    }

    return out;
}

export function removeBlanksCategories(input: (SubCategories | false)[]): SubCategories[] {
    return input.filter(i => !!i) as SubCategories[];
}